/**
 * Celebratory star row for the review prompt. The stars are an invitation,
 * not a score picker (store policy — specs/review-prompt AC-7): tapping any
 * star triggers the same accept action.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Pressable, View } from 'react-native';
import { Star } from 'lucide-react-native';
import { getReviewPromptModalStyles } from './ReviewPromptModal.styles';
import {
  REVIEW_PROMPT_COLORS,
  REVIEW_PROMPT_COPY,
  REVIEW_PROMPT_LAYOUT,
  REVIEW_PROMPT_STAR_COUNT,
  REVIEW_PROMPT_TIMING,
} from './reviewPromptModal.constants';

interface AnimatedStarProps {
  index: number;
  onPress: () => void;
  disabled: boolean;
}

const AnimatedStar: React.FC<AnimatedStarProps> = ({ index, onPress, disabled }) => {
  const styles = useMemo(() => getReviewPromptModalStyles(), []);
  const scale = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const spring = Animated.spring(scale, {
      toValue: 1,
      delay:
        REVIEW_PROMPT_TIMING.STAR_IN_BASE_DELAY_MS +
        index * REVIEW_PROMPT_TIMING.STAR_IN_STAGGER_MS,
      friction: 5,
      tension: 90,
      useNativeDriver: true,
    });
    spring.start();
    return () => spring.stop();
  }, [scale, index]);

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Pressable
        onPress={onPress}
        disabled={disabled}
        hitSlop={REVIEW_PROMPT_LAYOUT.STAR_HIT_SLOP}
        style={({ pressed }) => [styles.starButton, pressed && styles.starButtonPressed]}
        accessibilityRole="button"
        accessibilityLabel={REVIEW_PROMPT_COPY.A11Y_STAR}
        testID={`review-prompt-star-${index}`}
      >
        <Star
          size={REVIEW_PROMPT_LAYOUT.STAR_SIZE}
          color={REVIEW_PROMPT_COLORS.GOLD_DEEP}
          fill={REVIEW_PROMPT_COLORS.GOLD}
        />
      </Pressable>
    </Animated.View>
  );
};

interface ReviewPromptStarsProps {
  onPress: () => void;
  disabled: boolean;
}

export const ReviewPromptStars: React.FC<ReviewPromptStarsProps> = ({ onPress, disabled }) => {
  const styles = useMemo(() => getReviewPromptModalStyles(), []);
  return (
    <View style={styles.starsRow}>
      {Array.from({ length: REVIEW_PROMPT_STAR_COUNT }, (_, index) => (
        <AnimatedStar key={`star-${index}`} index={index} onPress={onPress} disabled={disabled} />
      ))}
    </View>
  );
};
